// lib/partners/leads.ts
// Formulario interno de las landings de captación (/partners y /agentes):
// validación, tipos de la fila y el contrato con GHL. Puro: sin I/O.
//
// Las tres puertas comparten tubería: la persona deja nombre y teléfono, el
// servidor guarda la fila en partner_leads, la enlaza al pipeline
// (crm_accounts, etiqueta `partner:<puerta>`), avisa al equipo y la manda al
// webhook de GHL para el SMS inmediato y la tarea «llamar».
import { z } from "zod"
import { normalizeUsPhone } from "@/lib/diagnostico/payload"

export { normalizeUsPhone }
import type { Database } from "@/lib/supabase/types"

export const PARTNER_DOORS = ["foodie", "referidor", "agente"] as const
export type PartnerDoor = (typeof PARTNER_DOORS)[number]

/** Versión del texto de la casilla. Si el texto cambia, cambia esto. */
export const PARTNER_DISCLOSURE_VERSION = "2026-10-09-v1"

/** Etiqueta con la que la ficha aparece (y se filtra) en /superadmin/pipeline. */
export function doorTag(door: PartnerDoor): string {
  return `partner:${door}`
}

export const DOOR_LABEL: Record<PartnerDoor, { es: string; en: string }> = {
  foodie: { es: "Foodie", en: "Foodie" },
  referidor: { es: "Referidor", en: "Referrer" },
  agente: { es: "Agente", en: "Agent" },
}

const phoneField = z.string().transform((v, ctx) => {
  const n = normalizeUsPhone(v)
  if (!n) ctx.addIssue({ code: "custom", message: "phone" })
  return n as string
})

/** Teléfono opcional: vacío → undefined; con valor inválido → issue en su propio campo. */
const optionalPhoneField = (issue: string) =>
  z
    .string()
    .trim()
    .transform((v, ctx) => {
      if (v === "") return undefined
      const n = normalizeUsPhone(v)
      if (!n) {
        ctx.addIssue({ code: "custom", message: issue })
        return undefined
      }
      return n
    })
    .optional()

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? undefined : v))
    .optional()

const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), { message: "email" })
  .transform((v) => (v === "" ? undefined : v))
  .optional()

/**
 * El esquema del formulario. `referralsPublicEnabled` (REFERRALS_PUBLIC_ENABLED)
 * decide si la puerta `referidor` es el registro propio del programa: con el
 * flag encendido exige la casilla de términos y el correo; apagado, la puerta
 * se comporta como siempre (solo nombre, teléfono y consentimiento).
 */
export function partnerLeadSchemaFor(opts: { referralsPublicEnabled: boolean }) {
  return z
    .object({
      door: z.enum(PARTNER_DOORS),
      name: z.string().trim().min(2).max(80),
      phone: phoneField,
      email: emailField,
      city: optionalText(80),
      note: optionalText(500),
      /** La casilla «acepto que me llamen y me escriban por SMS». Sin ella no se guarda. */
      consent_contact: z.literal(true, { message: "consent" }),
      lang: z.enum(["es", "en"]).default("es"),
      /** Honeypot: un humano no lo ve; la ruta responde 200 silencioso si trae valor. */
      website: z.string().max(500).optional(),
      /**
       * Solo puerta `referidor` con el programa público encendido: la casilla de
       * los términos (/partners/terminos). Obligatoria únicamente ahí.
       */
      program_terms: z.boolean().optional(),
      /** Solo puerta `referidor`: el restaurante que quiere presentar (todo opcional). */
      business_name: optionalText(160),
      business_owner: optionalText(120),
      // Con el programa apagado el restaurante no se usa: se ignora SIN validar,
      // para que un POST con el flag apagado jamás dé 400 por business_phone.
      business_phone: opts.referralsPublicEnabled ? optionalPhoneField("business_phone") : z.unknown().transform((): string | undefined => undefined).optional(),
    })
    .superRefine((v, ctx) => {
      if (!opts.referralsPublicEnabled || v.door !== "referidor") return
      if (v.program_terms !== true) ctx.addIssue({ code: "custom", path: ["program_terms"], message: "program_terms" })
      // El correo es el único canal para avisarle cuando su código esté activo.
      if (!v.email) ctx.addIssue({ code: "custom", path: ["email"], message: "email" })
    })
}
export type PartnerLeadInput = z.infer<ReturnType<typeof partnerLeadSchemaFor>>

export type DeliveryStatus = "pending" | "sent" | "failed" | "skipped"

export type PartnerLeadDbRow = Database["public"]["Tables"]["partner_leads"]["Row"]
export type PartnerLeadDbInsert = Database["public"]["Tables"]["partner_leads"]["Insert"]
export type PartnerLeadDbUpdate = Database["public"]["Tables"]["partner_leads"]["Update"]

/** La fila tal como la leen la ruta y la entrega (enums estrechos). */
export type PartnerLeadRow = Omit<PartnerLeadDbRow, "door" | "lang" | "ghl_status" | "ip"> & {
  door: PartnerDoor
  lang: "es" | "en"
  ghl_status: DeliveryStatus
  ip: string | null
}

/** Frontera BD → app. Los CHECK de la tabla garantizan los enums; es la ÚNICA conversión. */
export function rowFromDb(row: PartnerLeadDbRow): PartnerLeadRow {
  return row as unknown as PartnerLeadRow
}

/** Contrato con el workflow «Inbound Webhook → partners» de GHL. */
export function ghlPartnerPayload(row: PartnerLeadRow, appUrl: string): Record<string, unknown> {
  return {
    event: "partner_lead",
    lead_id: row.id,
    door: row.door,
    door_label: DOOR_LABEL[row.door].es,
    lang: row.lang,
    contact: { name: row.contact_name, phone: row.contact_phone, email: row.contact_email, city: row.city },
    note: row.note,
    consent_contact: row.consent_contact,
    disclosure_version: row.disclosure_version,
    consented_at: row.created_at,
    attribution: {
      utm_source: row.utm_source, utm_medium: row.utm_medium, utm_campaign: row.utm_campaign,
      utm_content: row.utm_content, utm_term: row.utm_term,
      gclid: row.gclid, gbraid: row.gbraid, wbraid: row.wbraid, fbclid: row.fbclid,
      landing_path: row.landing_path,
    },
    existing_customer: row.matched_merchant_id !== null,
    crm_url: row.account_id ? `${appUrl}/superadmin/pipeline/${row.account_id}` : null,
  }
}

/** La nota de la ficha (crm_activities, kind=system). */
export function activityBody(row: PartnerLeadRow): string {
  const parts = [`Formulario ${DOOR_LABEL[row.door].es.toLowerCase()} (${row.landing_path ?? (row.door === "agente" ? "/agentes" : "/partners")})`]
  if (row.note) parts.push(`«${row.note}»`)
  if (row.utm_campaign || row.utm_content) parts.push(`campaña ${[row.utm_campaign, row.utm_content].filter(Boolean).join(" · ")}`)
  return parts.join(" · ")
}
